import React from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShipByMmsi } from '@ais-ui/api';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { LoadingSpinner, Separator } from '@ais-ui/ui-core';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { useAuth } from '@ais-ui/auth';
import { useLengthUnits } from '../../Units/useLengthUnits';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div>
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className="text-xs">{isNonEmpty(value) ? value : '-'}</p>
    </div>
  );
};

interface IdentificationProps {
  shipInfo: VesselResult;
}

const Identification = ({ shipInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={shipInfo?.basic?.vesselName}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseIMOLabel}
        value={shipInfo?.basic?.vesselImoNumber}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={shipInfo?.basic?.vesselMmsiNumber}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCallSignLabel}
        value={shipInfo?.basic?.callSign}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={shipInfo?.basic?.activeFlag}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseShipTypeLabel}
        value={shipInfo?.basic?.vesselType}
      />
    </div>
  );
};

interface PhysicalCharacteristicsProps {
  shipInfo: VesselResult;
}

interface PhysicalCharacteristicsItemProps {
  label: string;
  value: number | undefined;
}

const PhysicalCharacteristicsItem = ({
  label,
  value,
}: PhysicalCharacteristicsItemProps) => {
  const { formatLength } = useLengthUnits();

  return (
    <div>
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className="text-xs">
        {isNonEmpty(value) ? `${formatLength(value)}` : '-'}
      </p>
    </div>
  );
};

const PhysicalCharacteristics = ({
  shipInfo,
}: PhysicalCharacteristicsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseWidthLabel}
        value={shipInfo?.basic?.vesselWidthMeters}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseLengthLabel}
        value={shipInfo?.basic?.vesselLengthMeters}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseDraughtLabel}
        value={shipInfo?.basic?.vesselDraught}
      />
    </div>
  );
};

interface PortCallItemProps {
  label: string;
  value: string;
}

const PortCallItem = ({ label, value }: PortCallItemProps) => {
  return (
    <div>
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className="text-xs">{isNonEmpty(value) ? value : '-'}</p>
    </div>
  );
};

type PortCallType = {
  ata: Date | null;
  atd: Date | null;
  port_name: string;
  un_location_code: string;
};

const PortCall = ({ portCall }: { portCall: PortCallType }) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="flex flex-col space-y-1">
      <h4 className="text-sm font-medium">{portCall.port_name}</h4>
      <div className="grid grid-cols-2 gap-1">
        <PortCallItem label="Locode" value={portCall.un_location_code} />
        <PortCallItem
          label="Actual Time of Arrival"
          value={formatDateTimeWithTimeZone(portCall.ata)}
        />
        <PortCallItem
          label="Actual Time of Departure"
          value={formatDateTimeWithTimeZone(portCall.atd)}
        />
      </div>
    </div>
  );
};

interface PortCallsProps {
  shipInfo: VesselResult;
}

const PortCalls = ({ shipInfo }: PortCallsProps) => {
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const { data, isLoading } = useGetShipByMmsi(
    extractMmsiFromShipInfo(shipInfo),
    hasShipDbPermissions
  );

  if (!hasShipDbPermissions) {
    return null;
  }

  return (
    <div className="space-y-2">
      {isLoading && <LoadingSpinner />}
      {data &&
        data.basic &&
        data.basic.portCalls &&
        [...data.basic.portCalls]
          .reverse()
          .map((portCall: PortCallType, index: number) => (
            <PortCall key={index} portCall={portCall} />
          ))}
    </div>
  );
};

interface BaseInformationProps {
  shipInfo: VesselResult;
}

export const ExtendedVesselBaseInformation = ({
  shipInfo,
}: BaseInformationProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();
  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  useGetShipByMmsi(extractMmsiFromShipInfo(shipInfo), hasShipDbPermissions);

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification shipInfo={shipInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Physical Characteristics</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <PhysicalCharacteristics shipInfo={shipInfo}></PhysicalCharacteristics>
      </div>
      {hasShipDbPermissions && (
        <div className="grid gap-1">
          <div>
            <div className="flex justify-between items-baseline pr-4">
              <h3 className="text-base font-medium">Port Calls</h3>
              <h3 className="text-sm text-muted-foreground">last 3</h3>
            </div>
            <Separator className="mt-2"></Separator>
          </div>
          <PortCalls shipInfo={shipInfo}></PortCalls>
        </div>
      )}
    </div>
  );
};
